import {SerializedStyles} from '@emotion/react';
import {css, Theme, useTheme} from '@mui/material';
import {DlMuiDynamicIcon, GetStylesFunction, IconHeaderWithSearch, LoadingSpinner, mixins, useLocalStorage} from '@wallaby-ui/core';
import {copyToClipboard, getDefaultMTOptions, MaterialTableWrapper, useFormatters} from '@wallaby-ui/document';
import {Column, Options} from 'material-table';
import {useSnackbar} from 'notistack';
import * as React from 'react';
import {useCallback, useMemo, useState} from 'react';

type UseTableScrollReturn = {
  defaultScroll: number;
  onScroll: (event: any) => void;
};

export function useUzisTableScroll(storageKey: string): UseTableScrollReturn {
  const [defaultScroll, setDefaultScroll] = useLocalStorage(storageKey, 0);
  const onScroll = useCallback((event: any) => setDefaultScroll((event.target as HTMLDivElement).scrollTop), [setDefaultScroll]);
  return {defaultScroll, onScroll};
}

export const useUzisTableStyles = (padding?: string) => {
  const theme = useTheme();
  return useMemo(() => getStyles({theme, padding}), [theme, padding]);
};

type ValueCellWithCopyProps = {
  value: number | string;
  formatInteger: (val: number) => string | undefined;
  allowString?: boolean;
  showCopyButton?: boolean;
};

export const ValueCellWithCopy: React.FC<ValueCellWithCopyProps> = ({value, formatInteger, allowString, showCopyButton = true}) => {
  const [isHovered, setIsHovered] = useState(false);
  const muiStyles = useUzisTableStyles();
  const snackbar = useSnackbar();
  const formatters = useFormatters();

  const handleCopy = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      await copyToClipboard(String(value), snackbar, formatters);
    },
    [value, snackbar, formatters]
  );

  if (typeof value === 'string') {
    return allowString ? <span>{value}</span> : <span>{value}</span>;
  }

  return (
    <div css={muiStyles.valueCellContent} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <span css={muiStyles.valueWithIcon}>
        {formatInteger(value)}

        {isHovered && showCopyButton && (
          <DlMuiDynamicIcon iconName='copyIcon' onClick={handleCopy} css={muiStyles.copyIcon} svgIconInputCss={muiStyles.copyIconSvg} />
        )}
      </span>
    </div>
  );
};

type UzisSimpleTableProps<RowT extends object> = {
  storageScrollKey: string;
  headerLabel: string; // i18n key
  columns: Column<RowT>[];
  data: RowT[];
  isLoading?: boolean;
  options?: Options<RowT> | undefined;
  tableAutoWidthStyle?: SerializedStyles;
};

export function UzisSimpleTable<RowT extends object>(props: UzisSimpleTableProps<RowT>) {
  const {storageScrollKey, headerLabel, columns, data, options, isLoading = true} = props;
  const muiStyles = useUzisTableStyles();
  const {defaultScroll, onScroll} = useUzisTableScroll(storageScrollKey);

  return (
    <div css={muiStyles.tableWrapper}>
      <IconHeaderWithSearch iconName='listIconMultiColor' label={headerLabel} useFixedViewBox='0 -1 16 14' iconCss={muiStyles.headerIcon} />
      {isLoading ? (
        // Render the spinner directly in the flex wrapper — the tableAutoWidth (inline-block, fit-content)
        // wrapper would otherwise collapse it to the left.
        <LoadingSpinner isVisible marginTop={0} wrapperStyles={{flexGrow: 1, alignContent: 'center'}} />
      ) : (
        <div css={[muiStyles.tableAutoWidth, props.tableAutoWidthStyle]}>
          <MaterialTableWrapper
            style={{maxHeight: '100%'}}
            defaultScroll={{to: defaultScroll}}
            onScroll={onScroll}
            options={{...getDefaultMTOptions(15), paging: false, ...(options || {})}}
            variant='big'
            useMaxHeight
            minColumnWidth={0} // PDH-1589: opt out — bespoke fit-content sizing + cellStyle widths
            columns={columns}
            data={data}
            isLoading={isLoading}
            totalCount={data.length}
          />
        </div>
      )}
    </div>
  );
}

type GetStylesInput = {
  theme: Theme;
  padding?: string;
};

const getStyles = ({theme, padding}: GetStylesInput) => ({
  tableAutoWidth: css`
    display: inline-block;
    width: fit-content;
    max-width: 100%;
    height: 100%;

    & table {
      width: auto !important;
      table-layout: fixed !important;
    }
    & .MuiPaper-root {
      width: fit-content;
      max-width: 100%;
    }
  `,
  tableWrapper: css`
    ${mixins.contentBox};
    border-radius: ${theme?.borderRadius};
    background: #ffffff;
    padding: ${padding ?? '16px'};
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 100%;
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
  valueCellContent: css`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 24px;
  `,
  valueCellText: css`
    white-space: nowrap;
  `,
  valueWithIcon: css`
    position: relative;
    display: inline-flex;
    align-items: center;
  `,
  copyIcon: css`
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translate(4px, -50%);

    padding: 0 !important;
    margin: 0 !important; // přidej
    width: 24px;
    height: 24px;
    min-width: 24px;
    min-height: 24px;

    display: inline-flex;
    align-items: center;
    justify-content: center;

    border: none !important;
    color: var(--dlTheme-500);
    cursor: pointer;

    & svg {
      display: block;
      margin: auto;
      width: 16px;
      height: 16px;
    }

    &:hover {
      color: var(--dlTheme-600);
    }
  `,
  copyIconSvg: css`
    font-size: 16px;
    display: block;
  `,
});
